/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var isHome = window.location.pathname === "/" || window.location.pathname === "";

  if (!isHome) {
    return;
  }

  function setHtml(selector, html) {
    var element = document.querySelector(selector);

    if (element) {
      element.innerHTML = html;
    }
  }

  function setText(selector, text) {
    var element = document.querySelector(selector);

    if (element) {
      element.textContent = text;
    }
  }

  function addHomepageCommentStyles() {
    if (document.getElementById("dd-homepage-comments-style")) {
      return;
    }

    var style = document.createElement("style");
    style.id = "dd-homepage-comments-style";
    style.textContent = [
      ".dd-home-hero__content{align-content:end!important;gap:clamp(18px,2.4vw,28px)!important;padding-top:clamp(72px,9vw,120px)!important}",
      ".dd-home-hero__copy{max-width:1160px!important;transform:translateY(clamp(-300px,-28vh,-170px))!important}",
      "@media (max-width:1199px){.dd-home-hero__copy{transform:translateY(clamp(-160px,-14vh,-90px))!important}}",
      "@media (max-width:740px){.dd-home-hero__copy{transform:translateY(-78px)!important}}",
      ".dd-home-hero h1{max-width:1160px!important}",
      ".dd-home-hero h1{font-size:clamp(46px,4.05vw,68px)!important;line-height:1.08!important}",
      ".dd-home-hero__quick-links{width:min(100%,1260px)!important;grid-template-columns:1.05fr 1fr 1.05fr 1.22fr 1.24fr!important;margin-top:0!important}",
      ".dd-home-hero__quick-copy,.dd-home-hero__quick-links a{line-height:1.08!important}",
      ".dd-pathway-card__hover{align-items:center!important;justify-content:center!important;text-align:center!important}",
      ".dd-pathway-card__hover strong{text-align:center!important}",
      ".dd-story-card__media .dd-story-card__image--tita{width:100%!important;max-width:min(100%,620px)!important;height:clamp(390px,35vw,540px)!important;padding:0!important;background:#fff!important}",
      "@media (max-width:740px){.dd-story-card__media .dd-story-card__image--tita{max-width:100%!important;height:clamp(310px,72vw,430px)!important}}"
    ].join("\n");
    document.head.appendChild(style);
  }

  function applyHomepageComments() {
    setHtml(".dd-home-hero h1", "Direct Drive Motors and<br>Wheeled Robot Platforms");

    var quickItems = document.querySelectorAll(".dd-home-hero__quick-copy strong");
    var quickLabels = [
      "D1 ROBOT<br>(D-Infinite)",
      "TITA ROBOT",
      "DIABLO ROBOT",
      "Direct Drive<br>Motors",
      "AGV/AMR<br>Wheel Modules"
    ];

    quickLabels.forEach(function (label, index) {
      if (quickItems[index]) {
        quickItems[index].innerHTML = label;
      }
    });

    var hoverItems = document.querySelectorAll(".dd-original-collections .dd-collection-hover-copy");
    var hoverLabels = [
      "<strong>D1 ROBOT PLATFORM</strong><em>The World's First Fully Modular Embodied Intelligent Robot.</em>",
      "<strong>TITA ROBOT PLATFORM</strong><em>The World's First 8DOF Wheeled Bipedal Robot.</em>",
      "<strong>DIABLO ROBOT PLATFORM</strong><em>The World's First Direct-Drive Self-Balancing Wheeled-Leg Robot.</em>",
      "<strong>M15 Direct Drive Motors</strong>",
      "<strong>M06 Direct Drive Motors</strong>"
    ];

    hoverLabels.forEach(function (label, index) {
      if (hoverItems[index]) {
        hoverItems[index].innerHTML = label;
      }
    });

    setText(".dd-home-showcase__block--applications .dd-section-kicker", "APPLICATIONS");
    setText(".dd-home-showcase__block--applications .dd-home-showcase__header h2", "We provide the professional modular platform solutions");
    setText(".dd-home-showcase__block--applications .dd-home-showcase__header > p:not(.dd-section-kicker)", "For embodied ROBOT, industrial and commercial robots, and home robots.");

    setText(".dd-home-showcase__block--resources .dd-section-kicker", "Development resources");
    setText(".dd-home-showcase__block--resources .dd-home-showcase__header h2", "Tutorials,SDKs, and engineering documentation");
    setText(".dd-home-showcase__block--resources .dd-home-showcase__header > p:not(.dd-section-kicker)", "Access the technical documentation required to integrate, test, and extend Direct Drive Tech ROBOT SERIES.");

    var resourceCards = document.querySelectorAll(".dd-resource-grid .dd-resource-card");
    var resourceItems = [
      {
        href: "/pages/d1-tutorials",
        title: "D1 Tutorials",
        body: "Unboxing video, robot tutorial videos, user manual, and Ubuntu development documentation for D1.",
        cta: "View tutorials"
      },
      {
        href: "/pages/discover-tita-how-to-videos-and-tutorials",
        title: "TITA Tutorials",
        body: "Unboxing video, robot tutorial videos, user manual, and Ubuntu development documentation for TITA.",
        cta: "View tutorials"
      },
      {
        href: "/pages/diablo-saukele",
        title: "DIABLO Tutorials",
        body: "Robot tutorial video, user manual, and SDK documentation for DIABLO development.",
        cta: "View tutorials"
      },
      {
        href: "https://github.com/DDTRobot",
        title: "GitHub",
        body: "SDK repositories, sample projects, and development resources for custom integrations.",
        cta: "View GitHub",
        external: true
      }
    ];

    resourceItems.forEach(function (item, index) {
      var card = resourceCards[index];

      if (!card) {
        return;
      }

      card.href = item.href;

      if (item.external) {
        card.target = "_blank";
        card.rel = "noopener";
      } else {
        card.removeAttribute("target");
        card.removeAttribute("rel");
      }

      var title = card.querySelector("strong");
      var body = card.querySelector("em");
      var cta = card.querySelector("b");

      if (title) {
        title.textContent = item.title;
      }

      if (body) {
        body.textContent = item.body;
      }

      if (cta) {
        cta.innerHTML = item.cta + " &rarr;";
      }
    });

    setText(".dd-story-card__panel--sample .dd-story-card__eyebrow", "2020");
    setText(".dd-story-card__panel--sample h2", "Company Established");
    setText(".dd-story-card__panel--sample p:not(.dd-story-card__eyebrow)", "Direct Drive Tech has completed registration and the technical research and development and Release the first product sample .");

    var sampleFacts = document.querySelector(".dd-story-card__panel--sample .dd-story-card__facts");

    if (sampleFacts) {
      sampleFacts.remove();
    }

    addHomepageCommentStyles();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyHomepageComments);
  } else {
    applyHomepageComments();
  }

  window.setTimeout(applyHomepageComments, 500);
  window.setTimeout(applyHomepageComments, 1500);
})();

(function () {
  var FILTER_INPUT_SELECTOR = "[data-dd-manual-filter]";
  var FILTER_CLEAR_SELECTOR = "[data-dd-manual-filter-clear]";
  var PRODUCT_SELECTOR = "product-list product-item[data-dd-filter-handle]";
  var EMPTY_SELECTOR = "[data-dd-manual-filter-empty]";
  var COUNT_SELECTOR = ".product-facet__meta-bar-item--count";

  function toDatasetKey(group) {
    return group.split("-").map(function (part) {
      return part.charAt(0).toUpperCase() + part.slice(1);
    }).join("");
  }

  function getSelectedFilters() {
    return Array.prototype.slice.call(document.querySelectorAll(FILTER_INPUT_SELECTOR)).reduce(function (groups, input) {
      var group = input.dataset.ddManualFilter;

      if (!groups[group]) {
        groups[group] = [];
      }

      if (input.checked) {
        groups[group].push(input.value);
      }

      return groups;
    }, {});
  }

  function productMatchesGroup(product, group, selectedValues) {
    if (!selectedValues.length) {
      return true;
    }

    var datasetKey = "ddFilter" + toDatasetKey(group);
    var productValues = (product.dataset[datasetKey] || "").split("|").filter(Boolean);

    return selectedValues.some(function (value) {
      return productValues.indexOf(value) !== -1;
    });
  }

  function updateManualFilterClearButtons(selectedFilters) {
    document.querySelectorAll(FILTER_CLEAR_SELECTOR).forEach(function (button) {
      var group = button.dataset.ddManualFilterClear;
      button.hidden = !selectedFilters[group] || selectedFilters[group].length === 0;
    });
  }

  function updateVisibleProductCount(visibleCount) {
    var countNode = document.querySelector(COUNT_SELECTOR);

    if (!countNode) {
      return;
    }

    var template = visibleCount === 1 ? countNode.dataset.ddCountOne : countNode.dataset.ddCountOther;

    if (!template) {
      template = "__count__ products";
    }

    countNode.textContent = template.replace("__count__", visibleCount);
  }

  function applyManualCollectionFilters() {
    var controls = Array.prototype.slice.call(document.querySelectorAll(FILTER_INPUT_SELECTOR));

    if (!controls.length) {
      return;
    }

    var selectedFilters = getSelectedFilters();
    var products = Array.prototype.slice.call(document.querySelectorAll(PRODUCT_SELECTOR));
    var visibleCount = 0;

    products.forEach(function (product) {
      var isVisible = Object.keys(selectedFilters).every(function (group) {
        return productMatchesGroup(product, group, selectedFilters[group]);
      });

      product.hidden = !isVisible;
      product.style.display = isVisible ? "" : "none";

      if (isVisible) {
        visibleCount += 1;
      }
    });

    updateManualFilterClearButtons(selectedFilters);
    updateVisibleProductCount(visibleCount);

    var emptyState = document.querySelector(EMPTY_SELECTOR);

    if (emptyState) {
      emptyState.hidden = visibleCount > 0;
    }
  }

  function scheduleManualCollectionFilters() {
    window.requestAnimationFrame(function () {
      applyManualCollectionFilters();
    });
  }

  window.directDriveManualCollectionFiltersApply = applyManualCollectionFilters;

  document.addEventListener("change", function (event) {
    var input = event.target.closest(FILTER_INPUT_SELECTOR);

    if (!input) {
      return;
    }

    event.stopPropagation();
    scheduleManualCollectionFilters();
  }, true);

  document.addEventListener("click", function (event) {
    var clearButton = event.target.closest(FILTER_CLEAR_SELECTOR);

    if (!clearButton) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var group = clearButton.dataset.ddManualFilterClear;

    document.querySelectorAll('[data-dd-manual-filter="' + group + '"]').forEach(function (input) {
      input.checked = false;
    });

    scheduleManualCollectionFilters();
  }, true);

  document.addEventListener("shopify:section:load", scheduleManualCollectionFilters);
  document.addEventListener("facet:criteria-changed", function () {
    window.setTimeout(scheduleManualCollectionFilters, 150);
  });
  window.addEventListener("pageshow", scheduleManualCollectionFilters);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleManualCollectionFilters);
  } else {
    scheduleManualCollectionFilters();
  }

  if ("MutationObserver" in window) {
    var observer = new MutationObserver(function (mutations) {
      var shouldApply = mutations.some(function (mutation) {
        return Array.prototype.slice.call(mutation.addedNodes).some(function (node) {
          return node.nodeType === Node.ELEMENT_NODE && (
            node.matches && (node.matches(PRODUCT_SELECTOR) || node.matches(FILTER_INPUT_SELECTOR)) ||
            node.querySelector && (node.querySelector(PRODUCT_SELECTOR) || node.querySelector(FILTER_INPUT_SELECTOR))
          );
        });
      });

      if (shouldApply) {
        scheduleManualCollectionFilters();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
})();

/* DirectDrive Japanese homepage visible text fallback 20260529 */
(function () {
  var isJapanesePage = window.location.pathname.indexOf("/ja-jp") === 0;

  if (!isJapanesePage && document.documentElement.lang) {
    isJapanesePage = document.documentElement.lang.toLowerCase().indexOf("ja") === 0;
  }

  if (!isJapanesePage && window.Shopify && window.Shopify.locale) {
    isJapanesePage = String(window.Shopify.locale).toLowerCase().indexOf("ja") === 0;
  }

  if (!isJapanesePage) {
    return;
  }

  function setHtml(selector, html) {
    var element = document.querySelector(selector);

    if (element) {
      element.innerHTML = html;
    }
  }

  function setText(selector, text) {
    var element = document.querySelector(selector);

    if (element) {
      element.textContent = text;
    }
  }

  function rewriteTextNodes(root, map) {
    if (!root) {
      return;
    }

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    var nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    nodes.forEach(function (node) {
      var text = node.nodeValue.trim();

      if (map[text]) {
        node.nodeValue = node.nodeValue.replace(text, map[text]);
      }
    });
  }

  function applyJapaneseHomepageText() {
    setHtml(".dd-home-hero h1", "ダイレクトドライブモーターと<br>ホイール型ロボットプラットフォーム");

    var quickItems = document.querySelectorAll(".dd-home-hero__quick-copy strong");
    var quickLabels = [
      "D1ロボット<br>（D-Infinite）",
      "TITAロボット",
      "DIABLOロボット",
      "ダイレクトドライブ<br>モーター",
      "AGV/AMR<br>ホイールモジュール"
    ];

    quickLabels.forEach(function (label, index) {
      if (quickItems[index]) {
        quickItems[index].innerHTML = label;
      }
    });

    var proofItems = document.querySelectorAll(".dd-home-hero__proof li");
    var proofLabels = ["技術サポート", "開発リソース", "安全な決済"];

    proofLabels.forEach(function (label, index) {
      if (!proofItems[index]) {
        return;
      }

      var labelNode = Array.prototype.slice.call(proofItems[index].childNodes).find(function (node) {
        return node.nodeType === Node.TEXT_NODE && node.nodeValue.trim();
      });

      if (labelNode) {
        labelNode.nodeValue = " " + label;
      }
    });

    var menuMap = {
      "Home": "ホーム",
      "Products": "製品",
      "Robots": "ロボット",
      "Motors": "モーター",
      "Tutorials": "チュートリアル",
      "D1 Tutorials": "D1チュートリアル",
      "TITA Tutorials": "TITAチュートリアル",
      "DIABLO Tutorials": "DIABLOチュートリアル",
      "Robotics Blog": "ロボティクスブログ",
      "About Us": "会社概要",
      "Contact": "お問い合わせ"
    };
    rewriteTextNodes(document.querySelector("header"), menuMap);
    rewriteTextNodes(document.querySelector(".mobile-nav"), menuMap);

    var contentMap = {
      "Wheeled Robots": "ホイール型ロボット",
      "Wheeled robots for research, secondary development, inspection, and delivery.": "研究、二次開発、点検、配送向けのホイール型ロボット。",
      "Explore more Wheeled Robots": "ホイール型ロボットを見る",
      "Motors": "モーター",
      "Suitable for household robots, embodied robots, fitness equipment, and entertainment equipment.": "家庭用ロボット、具身知能ロボット、フィットネス機器、エンターテインメント機器に適しています。",
      "Explore more Motors": "モーターを見る",
      "APPLICATIONS": "用途",
      "Hardware Applications": "用途",
      "We provide the professional modular platform solutions": "プロフェッショナル向けモジュール式プラットフォームソリューション",
      "Purpose-built hardware for advanced robotics applications": "プロフェッショナル向けモジュール式プラットフォームソリューション",
      "For embodied ROBOT, industrial and commercial robots, and home robots.": "具身知能ロボット、産業用・商業用ロボット、家庭用ロボット向け。",
      "Researching and Education": "研究・教育",
      "Secondary Development": "二次開発",
      "Inspection and Mapping Application": "点検・マッピング用途",
      "Delivery Application": "配送用途",
      "Development resources": "開発リソース",
      "Tutorials,SDKs, and engineering documentation": "チュートリアル、SDK、エンジニアリング資料",
      "Company Established": "会社設立",
      "Frequently Asked Questions": "よくある質問",
      "What are the advantages of direct drive motors?": "ダイレクトドライブモーターの利点は何ですか？",
      "What are the advantages of wheeled robots?": "ホイール型ロボットの利点は何ですか？",
      "What applications can our motor series be used in?": "モーターシリーズはどのような用途に使用できますか？",
      "What applications can our wheeled robot be used in?": "ホイール型ロボットはどのような用途に使用できますか？",
      "Please introduce the company team background.": "会社チームの背景を教えてください。",
      "Do you have your own production base?": "自社の生産拠点はありますか？",
      "Learn More": "詳細を見る",
      "LEARN MORE": "詳細を見る",
      "View tutorials": "チュートリアルを見る",
      "New arrival": "新製品"
    };
    rewriteTextNodes(document.querySelector("main") || document.body, contentMap);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyJapaneseHomepageText);
  } else {
    applyJapaneseHomepageText();
  }

  window.setTimeout(applyJapaneseHomepageText, 300);
  window.setTimeout(applyJapaneseHomepageText, 1200);
  window.setTimeout(applyJapaneseHomepageText, 2500);
})();

/* Remove the empty heading injected by the product reviews widget. */
(function () {
  function removeEmptyReviewHeadings() {
    document.querySelectorAll(".fera-reviews-widget-placeholder-header-left h2").forEach(function (heading) {
      if (!heading.textContent.trim()) {
        heading.remove();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", removeEmptyReviewHeadings);
  } else {
    removeEmptyReviewHeadings();
  }

  window.setTimeout(removeEmptyReviewHeadings, 500);
  window.setTimeout(removeEmptyReviewHeadings, 1500);

  if ("MutationObserver" in window) {
    new MutationObserver(removeEmptyReviewHeadings).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
})();
